import React, { useState } from "react";
import { Link, useParams, useHistory } from "react-router-dom";
import Logo from "../../../components/UI/Logo/Logo";
import Footer from "../../../components/Footer/Footer";
import PasswordFieldWithValidation from "../../../components/UI/PasswordFieldWithValidation";
import { Fade } from "react-awesome-reveal";

import {
  Card,
  Row,
  Col,
  Form,
  Input,
  Button,
  Checkbox,
  notification,
} from "antd";
import { LockOutlined } from "@ant-design/icons";
import { useResetPasswordMutation } from "../../../graphql/generated/graphql";

const Login = () => {
  //error state to control form
  const [hasError, setHasError] = useState(false);
  const [passwordValid, setPasswordValid] = useState(false);
  const [form] = Form.useForm();
  const [resetPasswordMutation, { loading }] = useResetPasswordMutation();
  const params: { email: string } = useParams();
  // console.log(params);
  const navigate = useHistory();

  // Watch password field value
  const password = Form.useWatch("password", form);

  //on submit login form
  const onFinish = (values: any) => {
    resetPasswordMutation({
      variables: {
        email: params.email.toLowerCase(),
        password: values.password,
      },
    })
      .then(({ data }) => {
        notification["success"]({
          message: "Your password has been successfully changed.",
        });
        navigate.push("/login", { passwordReset: true }); // Redirect to the login page with state
      })
      .catch((err) => {
        const errors = err.graphQLErrors.map((el: any) => el.message);
        notification["error"]({
          message: "Unable to Login",
          description: errors.map((er: any) => <div key={er}>{er}</div>),
          duration: 3,
          onClose: () => setHasError(false),
        });
      });
  };

  return (
    <>
      <div className="bg-primary" style={{ height: "100vh" }}>
        <div className="wrapper">
          <Row>
            <Col span={14}>
              <div className="text-center m-t-40">
                <img
                  src="http://blonk.co/demo/SOBlonk/assets/images/login-image.png"
                  className="img-fluid m-t-10"
                  alt=""
                />
              </div>
            </Col>
            <Col span={10}>
              <Card
                style={{
                  minHeight: "100vh",
                  marginBottom: "0px",
                  borderRadius: "0",
                }}
              >
                <div className="pb-4">
                  <h3 className="text-center m-t-40">
                    <Logo height="40" />
                  </h3>
                  <div className="p-3">
                    <p className="text-muted text-center pt-0 mtp0">
                      Do you remember your password ?{" "}
                      <Link to="/login" className="text-primary">
                        Login now
                      </Link>
                    </p>
                    <Fade>
                      <Form
                        name="normal_login"
                        className="login-form"
                        layout="vertical"
                        form={form}
                        onFieldsChange={() => setHasError(false)}
                        onFinish={onFinish}
                      >
                        <Form.Item
                          name="password"
                          label="Password"
                          rules={[
                            {
                              required: true,
                              message: "Please enter your new password!",
                            },
                            {
                              validator: (_, value) => {
                                if (!value) {
                                  return Promise.resolve();
                                }

                                // Check if password meets all requirements
                                const hasMinLength = value.length >= 8;
                                const hasNumber = /\d/.test(value);
                                const hasSpecialChar =
                                  /[!@#$%^&*()+\-=\[\]{}|;':",.<>?/\\]/.test(
                                    value
                                  );
                                const hasUppercase = /[A-Z]/.test(value);
                                const hasLowercase = /[a-z]/.test(value);

                                const allRequirementsMet =
                                  hasMinLength &&
                                  hasNumber &&
                                  hasSpecialChar &&
                                  hasUppercase &&
                                  hasLowercase;

                                if (allRequirementsMet) {
                                  return Promise.resolve();
                                }

                                // Show first unmet requirement (in order from screenshot)
                                if (!hasMinLength)
                                  return Promise.reject(
                                    "Password must be at least 8 characters"
                                  );
                                if (!hasNumber)
                                  return Promise.reject(
                                    "Password must contain number"
                                  );
                                if (!hasSpecialChar)
                                  return Promise.reject(
                                    "Password must contain special character"
                                  );
                                if (!hasUppercase)
                                  return Promise.reject(
                                    "Password must contain uppercase letter"
                                  );
                                if (!hasLowercase)
                                  return Promise.reject(
                                    "Password must contain lowercase letter"
                                  );

                                return Promise.resolve();
                              },
                            },
                          ]}
                          hasFeedback
                        >
                          <PasswordFieldWithValidation
                            prefix={
                              <LockOutlined className="site-form-item-icon" />
                            }
                            placeholder="Enter your password"
                            onValidationChange={(isValid) => {
                              setPasswordValid(isValid);
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          name="confirm"
                          label="Confirm Password"
                          dependencies={["password"]}
                          rules={[
                            {
                              required: true,
                              message: "Please confirm your password!",
                            },
                            {
                              min: 8,
                              message:
                                "Password must be at least 8 characters long",
                            },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (
                                  !value ||
                                  getFieldValue("password") === value
                                ) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(
                                  new Error("The two passwords do not match!")
                                );
                              },
                            }),
                          ]}
                          hasFeedback
                        >
                          <Input.Password
                            prefix={
                              <LockOutlined className="site-form-item-icon" />
                            }
                            type="password"
                            placeholder="Confirm your password"
                          />
                        </Form.Item>
                        <Form.Item>
                          <div className="text-right">
                            <Button
                              htmlType="submit"
                              className="ant-btn-amber"
                              shape="round"
                              disabled={
                                hasError ||
                                (!passwordValid &&
                                  password &&
                                  password.length > 0)
                              }
                              loading={loading}
                            >
                              Reset password
                            </Button>
                          </div>
                        </Form.Item>
                      </Form>
                    </Fade>
                  </div>
                </div>
                <Footer footer={false} />
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};

export default Login;
